import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { ExportAudience, TicketDetail } from '@/api/ticketsApi'
import { Field, NativeSelect } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useEscalateDeviation, useExportThread, useReopenTicket } from '@/hooks/useTickets'
import { buildPdfDocument, type PdfLabels } from '@/lib/exportPdf'
import { DEVIATION_DECISIONS } from '@/lib/resolveForm'
import { viewerTimeZone } from '@/lib/threadView'

interface DialogProps {
  ticket: TicketDetail
  routeId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation()
  if (!error) return null
  return (
    <p role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
      {t('common.error', { message: errorMessage(error) })}
    </p>
  )
}

/** Escalate to deviation: records the decision (and reference when raised). */
export function EscalateDialog({ ticket, routeId, open, onOpenChange }: DialogProps) {
  const { t } = useTranslation()
  const escalate = useEscalateDeviation(routeId)
  const [decision, setDecision] = useState(ticket.deviationDecision ?? 'raised')
  const [ref, setRef] = useState(ticket.deviationRef ?? '')
  const [missing, setMissing] = useState(false)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (decision === 'raised' && !ref.trim()) {
      setMissing(true)
      return
    }
    escalate.mutate(
      { decision, deviationRef: ref.trim() || undefined },
      {
        onSuccess: () => {
          onOpenChange(false)
          escalate.reset()
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('escalate.title')}</DialogTitle>
          <DialogDescription>{t('escalate.description')}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field id="esc-decision" label={t('escalate.decision')}>
            <NativeSelect id="esc-decision" value={decision} onChange={(e) => setDecision(e.target.value)}>
              {DEVIATION_DECISIONS.map((d) => (
                <option key={d} value={d}>
                  {t(`ticket.deviation.${d}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="esc-ref" label={t('escalate.ref')} error={missing && !ref.trim() ? t('escalate.refRequired') : null}>
            <Input
              id="esc-ref"
              className="font-mono"
              placeholder={t('escalate.refPlaceholder')}
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
          </Field>
          <ErrorLine error={escalate.error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={escalate.isPending}>
              {escalate.isPending ? t('escalate.submitting') : t('escalate.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Reopen a resolved ticket with an audited reason. */
export function ReopenDialog({ ticket, routeId, open, onOpenChange }: DialogProps) {
  const { t } = useTranslation()
  const reopen = useReopenTicket(routeId)
  const [reason, setReason] = useState('')
  const [missing, setMissing] = useState(false)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!reason.trim()) {
      setMissing(true)
      return
    }
    reopen.mutate(reason.trim(), {
      onSuccess: () => {
        onOpenChange(false)
        setReason('')
        reopen.reset()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('reopen.title', { key: ticket.key })}</DialogTitle>
          <DialogDescription>{t('reopen.description')}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field id="ro-reason" label={t('reopen.reason')} error={missing && !reason.trim() ? t('reopen.required') : null}>
            <Textarea
              id="ro-reason"
              rows={3}
              placeholder={t('reopen.reasonPlaceholder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <ErrorLine error={reopen.error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={reopen.isPending}>
              {reopen.isPending ? t('reopen.submitting') : t('reopen.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Export thread (PDF): fetches `/export` for the audience and renders it with jspdf. */
export function ExportDialog({ routeId, open, onOpenChange, canInternal }: Omit<DialogProps, 'ticket'> & { canInternal: boolean }) {
  const { t } = useTranslation()
  const exportThread = useExportThread(routeId)
  const [audience, setAudience] = useState<ExportAudience>('customer')

  const labels: PdfLabels = {
    thread: t('exportPdf.thread'),
    resolution: t('exportPdf.resolution'),
    attachments: t('exportPdf.attachments'),
    internalNote: t('exportPdf.internalNote'),
    via: (channel) => t('ticket.message.via', { channel }),
    resolvedBy: t('exportPdf.resolvedBy'),
    resolvedAt: t('exportPdf.resolvedAt'),
    category: t('exportPdf.category'),
    rootCause: t('exportPdf.rootCause'),
    deviation: t('exportPdf.deviation'),
    reason: t('exportPdf.reason'),
    summary: t('exportPdf.summary'),
    none: '—',
    audience: (a) => t('exportPdf.audience', { audience: t(`exportPdf.audienceName.${a}`) }),
    auditHead: (hash, verified) => t('exportPdf.auditHead', { hash, state: verified ? t('exportPdf.verified') : t('exportPdf.unverified') }),
    generated: (at, by) => t('exportPdf.generated', { at, by }),
    categoryLabel: (v) => t(`ticket.category.${v}`, { defaultValue: v }),
    deviationLabel: (v) => t(`ticket.deviation.${v}`, { defaultValue: v }),
    opened: (when, by) => t('exportPdf.opened', { when, by }),
  }

  function download() {
    const chosen = canInternal ? audience : 'customer'
    exportThread.mutate(chosen, {
      onSuccess: async (data) => {
        // jspdf is ~350 KB: load it only when someone exports.
        const { renderPdf } = await import('@/lib/exportPdfRender')
        renderPdf(buildPdfDocument(data, chosen, labels, viewerTimeZone()))
        onOpenChange(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('exportPdf.title')}</DialogTitle>
          <DialogDescription>{t('exportPdf.description')}</DialogDescription>
        </DialogHeader>
        {canInternal ? (
          <fieldset className="flex flex-col gap-2">
            {(['customer', 'internal'] as const).map((a) => (
              <label key={a} className="flex items-center gap-2 text-body text-ink">
                <input type="radio" name="export-audience" checked={audience === a} onChange={() => setAudience(a)} />
                {a === 'customer' ? t('exportPdf.audienceCustomer') : t('exportPdf.audienceInternal')}
              </label>
            ))}
          </fieldset>
        ) : null}
        <ErrorLine error={exportThread.error} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={download} disabled={exportThread.isPending}>
            {exportThread.isPending ? t('ticket.actions.exporting') : t('exportPdf.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
